import { useEffect, useRef } from 'react';
import { accountsApi } from '../services/api/endpoints/accounts';
import { useAccountingStore } from '../stores/accountingStore';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../stores/businessUnitStore';

const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Syncs the user's actual chart of accounts (for the current fiscal year)
 * into the global accounting store where AccountLookup can access it.
 * 
 * This replaces the old hardcoded SYSCOHADA_ACCOUNTS approach.
 * Call this once from a top-level component (e.g., Layout).
 */
export function useChartSync() {
  const { currentFiscalYear } = useFiscalYearStore();
  const { effectiveFilter } = useBusinessUnitStore();
  const { 
    chartFiscalYearId, 
    chartLastSynced, 
    setChartAccounts, 
    setChartLoading, 
    clearChart 
  } = useAccountingStore();
  const loadingRef = useRef(false);

  useEffect(() => {
    const fiscalYearId = currentFiscalYear?.id;
    if (!fiscalYearId) {
      clearChart();
      return;
    }

    // Skip if already cached and fresh for this FY
    const isCached = chartFiscalYearId === fiscalYearId && chartLastSynced;
    const isFresh = isCached && (Date.now() - chartLastSynced!) < CACHE_TTL_MS;
    if (isFresh || loadingRef.current) return;

    loadingRef.current = true;
    setChartLoading(true);

    // Fetch en pages successives. Le backend cappe `pageSize` à 100
    // (AccountFilterDto.@Max(100)) — l'ancien `pageSize: 5000` retournait
    // un 400 ValidationError silencieux et le store restait vide → toute
    // la page comptable était inutilisable (les selects de compte vides
    // poussaient les utilisateurs vers des saisies brutes type "411").
    // 10 pages de 100 = max 1000 comptes, largement assez pour un plan
    // SYSCOHADA complet (~500-700 entrées).
    const PAGE_SIZE = 100;
    const MAX_PAGES = 20; // borne dure de sécurité

    (async () => {
      try {
        const allAccounts: any[] = [];
        for (let page = 1; page <= MAX_PAGES; page++) {
          const response = await accountsApi.getAll({
            fiscalYear: fiscalYearId,
            pageSize: PAGE_SIZE,
            page,
            businessUnitId: effectiveFilter.businessUnitId,
            businessUnitType: effectiveFilter.businessUnitType,
          });
          if (!response.success || !response.data?.data) break;
          const batch = response.data.data;
          allAccounts.push(...batch);
          // Stop quand la dernière page est plus courte que PAGE_SIZE
          // (= pas de page suivante)
          if (batch.length < PAGE_SIZE) break;
        }
        setChartAccounts(allAccounts, fiscalYearId);
      } catch (err) {
        console.error('Failed to sync chart of accounts:', err);
      } finally {
        setChartLoading(false);
        loadingRef.current = false;
      }
    })();
  }, [currentFiscalYear?.id, effectiveFilter.businessUnitId, effectiveFilter.businessUnitType]);
}
